// src/components/CapabilitySidebar.tsx
import React from 'react';
import { Users, AlertTriangle, Target, DollarSign, Clock, Zap, TrendingUp, Brain, ChevronRight, Star, History, Settings, Code } from 'lucide-react';

interface CapabilitySidebarProps {
  onSelectCapability: (capabilityId: string) => void;
  selectedCapability: string | null;
}

const CapabilitySidebar: React.FC<CapabilitySidebarProps> = ({
  onSelectCapability,
  selectedCapability
}) => {
  const capabilities = [
    {
      id: 'team-rebalancing',
      name: 'Generate Capacity Plan',
      description: 'Predict personnel move impacts',
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      usage: 'Used 42 times',
      lastUsed: '2 hours ago'
    },
    {
      id: 'bottleneck-diagnosis',
      name: 'Automate Root Cause Analysis',
      description: 'Identify root causes of delays',
      icon: AlertTriangle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      usage: 'Used 28 times',
      lastUsed: '1 day ago'
    },
    {
      id: 'initiative-planner',
      name: 'Match Internal Talent',
      description: 'Optimally plan new initiatives',
      icon: Target,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      usage: 'Used 15 times',
      lastUsed: '3 days ago'
    },
    {
      id: 'budget-simulator',
      name: 'Optimize IT Budget',
      description: 'Simulate financial impacts',
      icon: DollarSign,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      usage: 'Used 23 times',
      lastUsed: '5 hours ago'
    },
    {
      id: 'ai-coding-impact',
      name: 'Generate AI Coding Impact',
      description: 'Measure AI tool productivity & ROI',
      icon: Code,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      usage: 'Used 18 times',
      lastUsed: '1 day ago'
    }
  ];

  const recentConversations = [
    { id: 1, query: 'Why is Payments project delayed?', time: '2:30 PM', capability: 'bottleneck-diagnosis' },
    { id: 2, query: 'Move Sarah to AI project', time: 'Yesterday', capability: 'team-rebalancing' },
    { id: 3, query: '10% budget cut simulation', time: '2 days ago', capability: 'budget-simulator' },
    { id: 4, query: 'Plan mobile app team', time: 'Last week', capability: 'initiative-planner' },
    { id: 5, query: 'AI coding tool ROI analysis', time: '3 hours ago', capability: 'ai-coding-impact' }
  ];

  const getCapabilityIcon = (capabilityId: string) => {
    const cap = capabilities.find(c => c.id === capabilityId);
    if (cap) {
      const Icon = cap.icon;
      return <Icon size={14} className={cap.color} />;
    }
    return <Brain size={14} className="text-gray-500" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center space-x-3">          
          <div>
            <h3 className="font-bold text-gray-900">Intelligence Hub</h3>
            <p className="text-xs text-gray-600">5 active capabilities</p>
          </div>
        </div>
      </div>

      {/* Capabilities List */}
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <Zap size={14} className="mr-2 text-yellow-500" />
          Intelligence Capabilities
        </h4>
        <div className="space-y-2">
          {capabilities.map((cap) => {
            const Icon = cap.icon;
            const isSelected = selectedCapability === cap.id;
            
            return (
              <button
                key={cap.id}
                onClick={() => onSelectCapability(cap.id)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  isSelected 
                    ? `${cap.bgColor} border ${cap.borderColor} ring-1 ring-offset-1 ${cap.color.replace('text', 'ring')}`
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${cap.bgColor}`}>
                      <Icon size={18} className={cap.color} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{cap.name}</p>
                      <p className="text-xs text-gray-500">{cap.description}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="p-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full">
                      <ChevronRight size={14} className="text-white" />
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <div className="flex items-center space-x-2">
                    <Clock size={12} />
                    <span>{cap.lastUsed}</span>
                  </div>
                  <span className="flex items-center space-x-1">
                    <TrendingUp size={12} />
                    <span>{cap.usage}</span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Footer */}
    </div>
  );
};

export default CapabilitySidebar;